import { IconCalendar, IconClock } from '@vfuk/source-system-icons';
import React, { useEffect, useState } from 'react';
import {
  customDateInputStyles,
  customTimeInputStyles,
  datePickerContainerStyles,
  datePickerStyles,
  hiddenInputStyles,
  iconCSS,
  nonRequiredStyles,
  pickerContainerStyles,
  pickerLabelStyles,
  separatorStyles,
  timePickerContainerStyles,
  timePickerStyles,
} from './styles/TimeSlotPicker.styles';
import DateTimePicker, { registerLocale } from 'react-datepicker';
import ar from 'date-fns/locale/ar';

const dateTimePickerDefaults = {
  datePickerTitle: 'Preferred date',
  datePickerPlaceholderText: 'DD | MM | YY',
  datePickerDateFormat: 'dd | MM | yy',
  timePickerTitle: 'Time',
  timePickerPlaceholderText: 'HH:mm',
  timePickerCaption: 'Time',
  optionalFieldIndicatorText: 'Optional',
  datePickerTimePickerSeparatorText: 'and',
};

const TimeSlotPicker: React.FC<{
  name: string;
  label: string;
  datePickerTitle: string;
  datePickerPlaceholderText: string;
  datePickerDateFormat: string;
  timePickerTitle: string;
  timePickerPlaceholderText: string;
  timePickerCaption: string;
  optionalFieldIndicatorText: string;
  datePickerTimePickerSeparatorText: string;
  isRequired?: boolean;
  value: string;
  onChange: Function;
  operatingTimes: {
    day: string;
    startTime: string;
    endTime: string;
  }[];
}> = ({
  name,
  label,
  datePickerTitle,
  datePickerPlaceholderText,
  datePickerDateFormat,
  timePickerTitle,
  timePickerPlaceholderText,
  timePickerCaption,
  optionalFieldIndicatorText,
  datePickerTimePickerSeparatorText,
  isRequired,
  value,
  onChange,
  operatingTimes,
}) => {
  const NOT_OPERATING_DAY = 'notOperatingDay';

  const setUpInitialDate = () => {
    let initialDate = new Date();
    initialDate.setDate(initialDate.getDate() + 1);
    initialDate.setHours(9);
    initialDate.setMinutes(0, 0, 0);
    let isWorkingDay = false;
    while (!isWorkingDay) {
      if (isOperatingDay(initialDate)) {
        isWorkingDay = true;
        initialDate = getWorkingHours(
          operatingTimes[initialDate.getDay()].startTime,
          initialDate
        )
        return initialDate;
      } else {
        initialDate.setDate(initialDate.getDate() + 1);
      }
    }
    return initialDate;
  };

  const initialDate = setUpInitialDate();
  const [datetime, setDatetime] = useState(null);
  const [registeredLocale, setRegisteredLocale] = useState<string>('');

  useEffect(() => {
    if (window.location.pathname.split('/').includes('ar')) {
      registerLocale('ar', ar);
      setRegisteredLocale('ar');
    }
  }, []);

  const handleDateChange = (date: Date) => {
    setDatetime((prevDatetime: Date) => {
      if (prevDatetime == null) {
        prevDatetime = initialDate;
      }
      const newDatetime = new Date(prevDatetime);
      newDatetime.setFullYear(date.getFullYear());
      newDatetime.setMonth(date.getMonth());
      newDatetime.setDate(date.getDate());
      triggerInput(newDatetime.toLocaleString());
      return newDatetime;
    });
  };

  const handleTimeChange = (time: Date) => {
    setDatetime((prevDatetime: Date) => {
      if (prevDatetime == null) {
        prevDatetime = initialDate;
      }
      const newDatetime = new Date(prevDatetime);
      newDatetime.setHours(time.getHours());
      newDatetime.setMinutes(time.getMinutes());
      newDatetime.setSeconds(time.getSeconds());
      triggerInput(newDatetime.toLocaleString());
      return newDatetime;
    });
  };

  const triggerInput = (enteredValue: string) => {
    const input = document.getElementById(name);
    const lastValue = input.value;
    input.value = enteredValue;
    const event = new Event('input', { bubbles: true });
    const tracker = input._valueTracker;

    if (tracker) {
      tracker.setValue(lastValue);
    }
    input.dispatchEvent(event);
  };

  function isOperatingDay(date: Date) {
    if (operatingTimes[date.getDay()]?.day === NOT_OPERATING_DAY) {
      return false;
    } else {
      return true;
    }
  }

  function getWorkingHours(timeString: String, date: Date) {
    const operatingTime = new Date(date);
    const [hours, minutes] = timeString.split(':').map(Number);
    operatingTime.setHours(hours);
    operatingTime.setMinutes(minutes);
    return operatingTime;
  };

  const isWorkHour = (date: Date) => {
    const hour = date.getHours();

    if (operatingTimes[date.getDay()]?.day !== NOT_OPERATING_DAY) {
      try {
        const operatingStart = getWorkingHours(
          operatingTimes[date.getDay()].startTime,
          date
        );
        const operatingEnd = getWorkingHours(
          operatingTimes[date.getDay()].endTime,
          date
        );
        return date >= operatingStart && date <= operatingEnd;
      } catch (e) {
        // using the default value if the Contentful setup is wrong
        console.log(e);
        return hour < 20 && hour > 6;
      }
    } else {
      return false;
    }
  };

  const CustomTimeInput = React.forwardRef<HTMLInputElement>((props, ref) => {
    return (
      <div className="time-input-container">
        <label style={{ display: 'flex', alignItems: 'center' }}>
          <input {...props} ref={ref} css={customTimeInputStyles} />
          <div css={iconCSS}>
            <IconClock size={2} />
          </div>
        </label>
      </div>
    );
  });

  const CustomDateInput = React.forwardRef<HTMLInputElement>((props, ref) => {
    return (
      <div className="date-input-container">
        <label style={{ display: 'flex', alignItems: 'center' }}>
          <input {...props} ref={ref} css={customDateInputStyles} />
          <div css={iconCSS}>
            <IconCalendar size={2} />
          </div>
        </label>
      </div>
    );
  });

  return (
    <div>
      <div css={pickerLabelStyles}>{label}</div>
      <div css={pickerContainerStyles}>
        <div css={datePickerContainerStyles}>
          {datePickerTitle || dateTimePickerDefaults.datePickerTitle}
          <div css={datePickerStyles}>
            <DateTimePicker
              selected={datetime}
              onChange={handleDateChange}
              placeholderText={
                datePickerPlaceholderText ||
                dateTimePickerDefaults.datePickerPlaceholderText
              }
              dateFormat={
                datePickerDateFormat ||
                dateTimePickerDefaults.datePickerDateFormat
              }
              minDate={initialDate}
              filterDate={isOperatingDay}
              customInput={<CustomDateInput />}
              locale={registeredLocale}
            />
          </div>
        </div>
        <div css={separatorStyles}>
          {datePickerTimePickerSeparatorText ||
            dateTimePickerDefaults.datePickerTimePickerSeparatorText}
        </div>
        <div css={timePickerContainerStyles}>
          {timePickerTitle || dateTimePickerDefaults.timePickerTitle}{' '}
          {!isRequired && (
            <span css={nonRequiredStyles}>
              {optionalFieldIndicatorText ||
                dateTimePickerDefaults.optionalFieldIndicatorText}
            </span>
          )}
          <div css={timePickerStyles}>
            <DateTimePicker
              selected={datetime}
              onChange={handleTimeChange}
              showTimeSelect
              showTimeSelectOnly
              timeIntervals={60}
              timeCaption={
                timePickerCaption || dateTimePickerDefaults.timePickerCaption
              }
              dateFormat="HH:mm"
              timeFormat="HH:mm"
              placeholderText={
                timePickerPlaceholderText ||
                dateTimePickerDefaults.timePickerPlaceholderText
              }
              filterTime={isWorkHour}
              customInput={<CustomTimeInput />}
            />
          </div>
        </div>
      </div>
      <input
        css={hiddenInputStyles}
        id={name}
        name={name}
        required={isRequired}
        value={value}
        data-testid={`${name}`}
        onChange={onChange}
      />
    </div>
  );
};

export default TimeSlotPicker;
